import { useState, useMemo } from 'react';
import { Tooltip } from '@mantine/core';
import { IconDots, IconChevronUp } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';

interface Props {
    bodyHtml?: string;
    bodyText?: string;
}

interface SplitResult {
    mainHtml: string | null;
    quotedHtml: string | null;
    mainText: string | null;
    quotedText: string | null;
    hasQuote: boolean;
}

function parseEmailContent(bodyHtml?: string, bodyText?: string): SplitResult {
    if (bodyHtml) {
        try {
            const parser = new DOMParser();
            const doc = parser.parseFromString(bodyHtml, 'text/html');

            const quoteSelector = [
                '.gmail_quote',
                '.gmail_extra',
                '#divRplyFwdMsg',
                '.OutlookMessageHeader',
                '#appendonsend',
                '.yahoo_quoted',
                'blockquote[type="cite"]',
                'blockquote.gmail_quote',
                'blockquote'
            ].join(', ');

            const quoteNodes = Array.from(doc.querySelectorAll(quoteSelector));

            if (quoteNodes.length > 0) {
                // Find top-level quote nodes so we don't duplicate nested ones
                const topLevelQuotes: Element[] = [];
                for (const node of quoteNodes) {
                    const isNested = topLevelQuotes.some((parent) => parent.contains(node));
                    if (!isNested) {
                        topLevelQuotes.push(node);
                    }
                }

                if (topLevelQuotes.length > 0) {
                    const quotesWithAttr: Element[] = [];
                    for (const quoteNode of topLevelQuotes) {
                        // Check if previous sibling is an attribution line (e.g. "On ... wrote:")
                        const prev = quoteNode.previousElementSibling;
                        if (prev && (
                            prev.classList.contains('gmail_attr') ||
                            /^(On\s+[\s\S]+?wrote:|El\s+[\s\S]+?escribi[óo]:|Em\s+[\s\S]+?escreveu:|From:\s+|De:\s+|-----Original)/i.test((prev.textContent || '').trim())
                        )) {
                            quotesWithAttr.push(prev);
                        }
                        quotesWithAttr.push(quoteNode);

                        // In Outlook, following siblings in the container are part of the forward/reply
                        if (quoteNode.id === 'divRplyFwdMsg' || quoteNode.classList.contains('OutlookMessageHeader')) {
                            let next = quoteNode.nextElementSibling;
                            while (next) {
                                const toAdd = next;
                                next = next.nextElementSibling;
                                quotesWithAttr.push(toAdd);
                            }
                        }
                    }

                    // Check if doc without quotes has remaining content
                    const docClone = doc.cloneNode(true) as Document;
                    for (const n of Array.from(docClone.querySelectorAll(quoteSelector))) {
                        n.remove();
                    }
                    const remainingText = (docClone.body.textContent || '').replace(/\s+/g, ' ').trim();

                    // If remainingText is not empty, we can safely separate the quote
                    if (remainingText) {
                        const quotedContainer = doc.createElement('div');
                        for (const node of quotesWithAttr) {
                            quotedContainer.appendChild(node.cloneNode(true));
                            node.remove();
                        }

                        return {
                            mainHtml: doc.body.innerHTML,
                            quotedHtml: quotedContainer.innerHTML,
                            mainText: null,
                            quotedText: null,
                            hasQuote: true
                        };
                    }
                }
            }

            return {
                mainHtml: bodyHtml,
                quotedHtml: null,
                mainText: null,
                quotedText: null,
                hasQuote: false
            };
        } catch {
            return {
                mainHtml: bodyHtml,
                quotedHtml: null,
                mainText: null,
                quotedText: null,
                hasQuote: false
            };
        }
    }

    if (bodyText) {
        const quoteRegex = /(?:\r?\n|^)(?:On\s+[\s\S]+?wrote:|El\s+[\s\S]+?escribi[óo]:|Em\s+[\s\S]+?escreveu:|-----Original Message-----|--- Mensaje original ---|(?:\r?\n>\s*)+)/i;
        const match = bodyText.match(quoteRegex);

        if (match && match.index !== undefined) {
            const mainPart = bodyText.slice(0, match.index).trimEnd();
            const quotedPart = bodyText.slice(match.index).trim();

            if (mainPart.trim()) {
                return {
                    mainHtml: null,
                    quotedHtml: null,
                    mainText: mainPart,
                    quotedText: quotedPart,
                    hasQuote: true
                };
            }
        }

        return {
            mainHtml: null,
            quotedHtml: null,
            mainText: bodyText,
            quotedText: null,
            hasQuote: false
        };
    }

    return {
        mainHtml: null,
        quotedHtml: null,
        mainText: '',
        quotedText: null,
        hasQuote: false
    };
}

const purifyConfig = {
    ADD_DATA_URI_TAGS: ['img'],
    ADD_ATTR: ['target', 'style', 'src']
};

export default function EmailMessageBody({ bodyHtml, bodyText }: Props): JSX.Element {
    const { t } = useTranslation();
    const [showQuoted, setShowQuoted] = useState(false);

    const parsed = useMemo(() => parseEmailContent(bodyHtml, bodyText), [bodyHtml, bodyText]);

    return (
        <div className="text-xs text-gray-800 leading-relaxed overflow-x-auto py-0.5">
            {/* Main Message Content */}
            {parsed.mainHtml ? (
                <div
                    className="prose prose-xs max-w-none break-words text-gray-800 [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded-lg [&_img]:my-2 [&_img]:border [&_img]:border-gray-200 [&_img]:shadow-xs"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(parsed.mainHtml, purifyConfig) }}
                />
            ) : (
                <div className="whitespace-pre-wrap font-sans break-words text-gray-800">
                    {parsed.mainText}
                </div>
            )}

            {/* Quoted Thread Toggle Button */}
            {parsed.hasQuote && (
                <div className="mt-2">
                    <Tooltip label={showQuoted ? t('hide_quoted_text') : t('show_quoted_text')}>
                        <button
                            type="button"
                            onClick={() => setShowQuoted(!showQuoted)}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium text-gray-500 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer border border-gray-200/60"
                        >
                            <IconDots size={13} />
                        </button>
                    </Tooltip>
                </div>
            )}

            {/* Expanded Quoted Thread */}
            {parsed.hasQuote && showQuoted && (
                <div className="mt-2.5 pt-2.5 border-t border-dashed border-gray-200 pl-3 border-l-2 border-l-gray-300 text-gray-500 text-xs">
                    <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                            {t('quoted_text')}
                        </span>
                        <button
                            type="button"
                            onClick={() => setShowQuoted(false)}
                            className="inline-flex items-center gap-1 text-[10px] text-gray-400 hover:text-gray-600 cursor-pointer"
                        >
                            <IconChevronUp size={12} />
                            {t('hide_quoted_text')}
                        </button>
                    </div>

                    {parsed.quotedHtml ? (
                        <div
                            className="prose prose-xs max-w-none break-words text-gray-500 overflow-x-auto opacity-80 [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded-lg [&_img]:my-2 [&_img]:border [&_img]:border-gray-200"
                            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(parsed.quotedHtml, purifyConfig) }}
                        />
                    ) : (
                        <div className="whitespace-pre-wrap font-sans break-words text-[11px] text-gray-500 opacity-80">
                            {parsed.quotedText}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
